import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Camera, ChevronLeft, ChevronRight, X, Trash2, Loader2, Monitor } from 'lucide-react';
import EmployeeLink from '../components/EmployeeLink';
import { useDisplaySettings } from '../hooks/useDisplaySettings';

const PAGE_SIZE = 24;

function activityBadgeClass(pct) {
  if (pct >= 75) return 'bg-green-100 text-green-700';
  if (pct >= 40) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

function formatTime(iso, tz = 'UTC', timeFormat = '12h') {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: timeFormat === '12h' });
}

function formatDateHeader(iso, tz = 'UTC') {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz });
}

// ---- lightbox ----
function Lightbox({ screenshots, initialIndex, onClose, tz = 'UTC', timeFormat = '12h' }) {
  const [index, setIndex] = useState(initialIndex);
  const indexRef = useRef(index);
  const onCloseRef = useRef(onClose);

  // Keep refs in sync
  useEffect(() => {
    indexRef.current = index;
    onCloseRef.current = onClose;
  }, [index, onClose]);

  const ss = screenshots[index];
  const hasPrev = index > 0;
  const hasNext = index < screenshots.length - 1;

  const prev = () => {
    if (indexRef.current > 0) {
      setIndex(indexRef.current - 1);
    }
  };

  const next = () => {
    if (indexRef.current < screenshots.length - 1) {
      setIndex(indexRef.current + 1);
    }
  };

  // Keyboard navigation with stable deps
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Main container */}
      <div
        className="max-w-5xl w-full max-h-[95vh] flex flex-col items-center gap-4 bg-black bg-opacity-60 rounded-2xl p-6 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="relative flex-1 flex items-center justify-center w-full overflow-hidden">
          <img
            src={ss?.imageUrl}
            alt="Screenshot"
            className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain"
          />

          {/* Prev button */}
          {hasPrev && (
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-90 text-gray-900 rounded-full hover:bg-opacity-100 transition-all hover:scale-110 shadow-lg"
              title="Previous (←)"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next button */}
          {hasNext && (
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-90 text-gray-900 rounded-full hover:bg-opacity-100 transition-all hover:scale-110 shadow-lg"
              title="Next (→)"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Info footer */}
        <div className="flex flex-col items-center gap-2 w-full">
          <div className="flex items-center gap-4 text-white text-sm">
            <span className="font-medium">{ss?.user?.name || ss?.userName || '--'}</span>
            <span className="text-gray-300">{formatTime(ss?.takenAt, tz, timeFormat)}</span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${activityBadgeClass(ss?.activityPct || 0)}`}
            >
              {Math.round(ss?.activityPct || 0)}% activity
            </span>
            <span className="text-gray-400">
              {index + 1} / {screenshots.length}
            </span>
          </div>
          {ss?.windowTitle && (
            <div className="flex items-center gap-1.5 text-gray-400 text-xs max-w-2xl">
              <Monitor className="w-3 h-3 flex-shrink-0" />
              <span className="truncate" title={ss.windowTitle}>{ss.windowTitle}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- purge old confirmation ----
function PurgeConfirmDialog({ onConfirm, onCancel, isLoading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100">
            <Trash2 className="w-5 h-5 text-amber-600" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">Delete Old Screenshots</h2>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          This will permanently delete all screenshots older than 3 months. Recent screenshots will not be affected. This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-2 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-medium"
          >
            {isLoading ? 'Deleting...' : 'Delete Old Screenshots'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Group screenshots by user+date key
function groupScreenshots(screenshots) {
  const groups = {};
  for (const ss of screenshots) {
    const userName = ss.user?.name || ss.userName || 'Unknown';
    const userId = ss.user?.id || ss.userId || null;
    const date = ss.takenAt ? new Date(ss.takenAt).toDateString() : 'Unknown';
    const key = `${userId || userName}__${date}`;
    if (!groups[key]) groups[key] = { userName, userId, date: ss.takenAt, items: [] };
    groups[key].items.push(ss);
  }
  return Object.values(groups).sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
}

export default function Screenshots() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { timezone: tz, timeFormat } = useDisplaySettings();

  const [filters, setFilters] = useState({ userId: '', date: '' });
  const [lightbox, setLightbox] = useState(null); // { screenshots, index }
  const [showPurge, setShowPurge] = useState(false);

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
  });

  const buildParams = (pageParam = 1) => {
    const p = new URLSearchParams({ page: String(pageParam), limit: String(PAGE_SIZE) });
    if (filters.userId) p.set('userId', filters.userId);
    if (filters.date) p.set('date', filters.date);
    return p.toString();
  };

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['screenshots', filters],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await api.get(`/api/screenshots?${buildParams(pageParam)}`);
      return res.data;
    },
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((acc, p) => acc + (p.data?.length || 0), 0);
      return loaded < (lastPage.total || 0) ? pages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });

  const allScreenshots = (data?.pages || []).flatMap((p) => p.data || []);
  const groups = groupScreenshots(allScreenshots);

  const purgeOldMutation = useMutation({
    mutationFn: () => api.delete('/api/screenshots/purge-old'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['screenshots'] });
      setShowPurge(false);
    },
  });

  const openLightbox = (groupItems, itemIndex) => {
    setLightbox({ screenshots: groupItems, index: itemIndex });
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load screenshots.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filters + actions */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">User</label>
          <select
            value={filters.userId}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Users</option>
            {(usersData || []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
          <input
            type="date"
            value={filters.date}
            onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowPurge(true)}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 text-sm font-medium rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Old Screenshots
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-video bg-gray-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : allScreenshots.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Camera className="w-10 h-10 text-gray-300" />
          <p className="text-sm text-gray-400">No screenshots found.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <div key={`${group.userName}-${group.date}`}>
              <div className="flex items-baseline gap-2 mb-3">
                <EmployeeLink userId={group.userId} name={group.userName} className="text-sm font-semibold text-gray-900" />
                <span className="text-xs text-gray-400">{formatDateHeader(group.date, tz)}</span>
                <span className="text-xs text-gray-400">({group.items.length})</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {group.items.map((ss, idx) => (
                  <div
                    key={ss.id}
                    onClick={() => openLightbox(group.items, idx)}
                    className="relative group cursor-pointer rounded-xl overflow-hidden bg-gray-200 aspect-video border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <img
                      src={ss.imageUrl}
                      alt="Screenshot"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
                    {/* Bottom info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent px-2 py-2">
                      <div className="flex items-end justify-between">
                        <span className="text-white text-xs font-medium">{formatTime(ss.takenAt, tz, timeFormat)}</span>
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-medium ${activityBadgeClass(ss.activityPct || 0)}`}
                        >
                          {Math.round(ss.activityPct || 0)}%
                        </span>
                      </div>
                      {ss.windowTitle && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Monitor className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />
                          <span className="text-[10px] text-gray-300 truncate" title={ss.windowTitle}>{ss.windowTitle}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      {hasNextPage && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-60 transition-colors"
          >
            {isFetchingNextPage && <Loader2 className="w-4 h-4 animate-spin" />}
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          screenshots={lightbox.screenshots}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
          tz={tz}
          timeFormat={timeFormat}
        />
      )}

      {/* Purge old confirm */}
      {showPurge && (
        <PurgeConfirmDialog
          onConfirm={() => purgeOldMutation.mutate()}
          onCancel={() => setShowPurge(false)}
          isLoading={purgeOldMutation.isPending}
        />
      )}
    </div>
  );
}
